import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
  Badge,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  Palette as PaletteIcon,
  BarChart as BarChartIcon,
  PriorityHigh as PriorityIcon,
  ErrorOutline as ErrorIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Store as StoreIcon,
  People as PeopleIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  ManageSearch as ManageSearchIcon,
  Inventory2 as Inventory2Icon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import { getCurrentMonthYearBrasilia } from '../../utils/dateUtils';
import NotificationBell from './NotificationBell';
import ChangelogModal from '../ChangelogModal';
import { APP_VERSION } from '../../config/changelog';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuGroup {
  title: string;
  icon: React.ReactNode;
  color: string;
  items: MenuItem[];
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  roles: string[];
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [orderCount, setOrderCount] = useState(0);
  const [refundCount, setRefundCount] = useState(0);
  const [lostOrdersCount, setLostOrdersCount] = useState(0);
  const [delayedOrdersCount, setDelayedOrdersCount] = useState(0);
  const [internalErrorsCount, setInternalErrorsCount] = useState(0);
  const [verificarCount, setVerificarCount] = useState(0);
  const [reposicaoCount, setReposicaoCount] = useState(0);
  const [trocadosCount, setTrocadosCount] = useState(0);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({
    gestao: true,
    vendas: false,
    operacoes: false,
  });
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Função para buscar todos os contadores dos badges (filtrados pelo mês atual).
  // IMPORTANTE: cada contador é buscado de forma ISOLADA. Alguns endpoints são restritos
  // por perfil (ex.: erros internos só ADMIN/SUPPORT/MANAGER) e retornam 403 para os demais.
  // Em sequência num único try/catch, esse 403 interrompia tudo e os contadores seguintes
  // (como "Verificar Pedido") nunca eram atualizados para Vendedor/Designer/Produção.
  const fetchAllBadgeCounts = useCallback(async () => {
    const { month, year } = getCurrentMonthYearBrasilia();
    const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    // Promise.allSettled nunca rejeita: cada falha fica contida no seu próprio bloco
    await Promise.allSettled([
      // Prioridades ativas (excluindo FINALIZED e CANCELLED)
      (async () => {
        const orders = await apiService.getAllOrders();
        const activeOrders = (orders || []).filter((o: any) => o.status !== 'FINALIZED' && o.status !== 'CANCELLED');
        setOrderCount(activeOrders.length);
      })(),
      // Estornos pendentes (sem filtro de mês)
      (async () => {
        const refunds = await apiService.getAllRefundOrders();
        setRefundCount((refunds || []).filter((r: any) => r.status === 'ESTORNAR').length);
      })(),
      // Pedidos extraviados do mês
      (async () => {
        const lostOrders = await apiService.getAllLostOrders(startDate, endDate);
        setLostOrdersCount((lostOrders || []).length);
      })(),
      // Pedidos atrasados do mês
      (async () => {
        const delayedOrders = await apiService.getAllDelayedOrders(startDate, endDate);
        setDelayedOrdersCount((delayedOrders || []).length);
      })(),
      // Erros internos do mês (endpoint restrito — falha silenciosa nos demais perfis)
      (async () => {
        const internalErrors = await apiService.getAllInternalErrors(startDate, endDate);
        setInternalErrorsCount((internalErrors || []).length);
      })(),
      // Pedidos na situação "Verificar" (sem filtro de mês) — disponível a todos os perfis
      (async () => {
        const toVerify = await apiService.getAllOrderVerifications(undefined, undefined, 'Verificar');
        setVerificarCount((toVerify || []).length);
      })(),
      // Reposições ainda não finalizadas (sem filtro de mês) — disponível a todos os perfis
      (async () => {
        const pending = await apiService.getAllOrderReplacements(undefined, undefined, undefined, true);
        setReposicaoCount((pending || []).length);
      })(),
      // Pedidos trocados ainda "Em análise" (sem filtro de mês) — disponível a todos os perfis
      (async () => {
        const pending = await apiService.getAllOrderSwaps(undefined, undefined, undefined, true);
        setTrocadosCount((pending || []).length);
      })(),
    ]);
  }, []);

  // Buscar contadores ao montar o componente e atualizar periodicamente
  useEffect(() => {
    if (!user) return;

    // Buscar imediatamente
    fetchAllBadgeCounts();

    // Configurar intervalo para atualizar a cada 30 segundos
    const interval = setInterval(() => {
      fetchAllBadgeCounts();
    }, 30000); // 30 segundos

    // Limpar intervalo ao desmontar
    return () => clearInterval(interval);
  }, [user, fetchAllBadgeCounts]);

  // Exibir popup de atualizações quando a versão for nova para o usuário
  useEffect(() => {
    if (!user) return;
    const STORAGE_KEY = 'crm_seen_version';
    const seenVersion = localStorage.getItem(STORAGE_KEY);
    if (seenVersion !== APP_VERSION) {
      setChangelogOpen(true);
    }
  }, [user]);

  const handleCloseChangelog = () => {
    localStorage.setItem('crm_seen_version', APP_VERSION);
    setChangelogOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  const handleGroupClick = (groupKey: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Menu groups based on user role
  const getMenuGroups = (): MenuGroup[] => {
    if (user?.role === 'ADMIN') {
      return [
        {
          title: 'GESTÃO',
          icon: <DashboardIcon />,
          color: '#6366f1',
          items: [
            {
              text: 'Painel',
              icon: <DashboardIcon />,
              path: '/dashboard',
              roles: ['ADMIN']
            },
            {
              text: 'Faturamento',
              icon: <ReceiptIcon />,
              path: '/billing',
              roles: ['ADMIN']
            },
            {
              text: 'Orçamento Rápido',
              icon: <SpeedIcon />,
              path: '/quick-quote',
              roles: ['ADMIN']
            },
          ]
        },
        {
          title: 'VENDAS',
          icon: <TrendingUpIcon />,
          color: '#10b981',
          items: [
            {
              text: 'Desempenho Vendas',
              icon: <TrendingUpIcon />,
              path: '/sales-by-seller',
              roles: ['ADMIN']
            },
            {
              text: 'Atendimento Arte',
              icon: <PaletteIcon />,
              path: '/designer-service',
              roles: ['ADMIN']
            },
            {
              text: 'Desempenho Arte',
              icon: <BarChartIcon />,
              path: '/designer-productivity',
              roles: ['ADMIN']
            },
          ]
        },
        {
          title: 'OPERAÇÕES',
          icon: <PriorityIcon />,
          color: '#f59e0b',
          items: [
            {
              text: 'Controle Prioridades',
              icon: <PriorityIcon />,
              path: '/priority-control',
              badge: orderCount,
              roles: ['ADMIN', 'SELLER', 'DESIGNER', 'SUPPORT']
            },
            {
              text: 'Pedidos Extraviados',
              icon: <ErrorIcon />,
              path: '/lost-orders',
              badge: lostOrdersCount,
              roles: ['ADMIN']
            },
            {
              text: 'Pedidos Atrasados',
              icon: <ScheduleIcon />,
              path: '/delayed-orders',
              badge: delayedOrdersCount,
              roles: ['ADMIN']
            },
            {
              text: 'Erros Internos',
              icon: <CancelIcon />,
              path: '/internal-errors',
              badge: internalErrorsCount,
              roles: ['ADMIN']
            },
            {
              text: 'Pedidos de Estornos',
              icon: <RefreshIcon />,
              path: '/refund-orders',
              badge: refundCount,
              roles: ['ADMIN']
            },
            {
              text: 'Verificar Pedido',
              icon: <ManageSearchIcon />,
              path: '/verificar-pedido',
              badge: verificarCount,
              roles: ['ADMIN']
            },
            {
              text: 'Reposição',
              icon: <Inventory2Icon />,
              path: '/reposicao',
              badge: reposicaoCount,
              roles: ['ADMIN']
            },
            {
              text: 'Pedidos Trocados',
              icon: <SwapHorizIcon />,
              path: '/pedidos-trocados',
              badge: trocadosCount,
              roles: ['ADMIN']
            },
          ]
        },
      ];
    }

    if (user?.role === 'SELLER') {
      return [
        {
          title: 'VENDAS',
          icon: <TrendingUpIcon />,
          color: '#10b981',
          items: [
            {
              text: 'Minhas Vendas',
              icon: <TrendingUpIcon />,
              path: '/seller-sales',
              roles: ['SELLER']
            },
            {
              text: 'Orçamento Rápido',
              icon: <SpeedIcon />,
              path: '/quick-quote',
              roles: ['SELLER']
            },
          ]
        },
        {
          title: 'OPERAÇÕES',
          icon: <PriorityIcon />,
          color: '#f59e0b',
          items: [
            {
              text: 'Controle Prioridades',
              icon: <PriorityIcon />,
              path: '/priority-control',
              badge: orderCount,
              roles: ['SELLER']
            },
            {
              text: 'Verificar Pedido',
              icon: <ManageSearchIcon />,
              path: '/verificar-pedido',
              badge: verificarCount,
              roles: ['SELLER']
            },
            {
              text: 'Reposição',
              icon: <Inventory2Icon />,
              path: '/reposicao',
              badge: reposicaoCount,
              roles: ['SELLER']
            },
            {
              text: 'Pedidos Trocados',
              icon: <SwapHorizIcon />,
              path: '/pedidos-trocados',
              badge: trocadosCount,
              roles: ['SELLER']
            },
          ]
        },
      ];
    }

    if (user?.role === 'DESIGNER') {
      return [
        {
          title: 'ARTE',
          icon: <PaletteIcon />,
          color: '#ec4899',
          items: [
            {
              text: 'Painel',
              icon: <DashboardIcon />,
              path: '/dashboard',
              roles: ['DESIGNER']
            },
            {
              text: 'Meus Pedidos',
              icon: <PaletteIcon />,
              path: '/designer-interface',
              roles: ['DESIGNER']
            },
            {
              text: 'Orçamento Rápido',
              icon: <SpeedIcon />,
              path: '/quick-quote',
              roles: ['DESIGNER']
            },
          ]
        },
        {
          title: 'OPERAÇÕES',
          icon: <PriorityIcon />,
          color: '#f59e0b',
          items: [
            {
              text: 'Controle Prioridades',
              icon: <PriorityIcon />,
              path: '/priority-control',
              badge: orderCount,
              roles: ['DESIGNER']
            },
            {
              text: 'Verificar Pedido',
              icon: <ManageSearchIcon />,
              path: '/verificar-pedido',
              badge: verificarCount,
              roles: ['DESIGNER']
            },
            {
              text: 'Reposição',
              icon: <Inventory2Icon />,
              path: '/reposicao',
              badge: reposicaoCount,
              roles: ['DESIGNER']
            },
            {
              text: 'Pedidos Trocados',
              icon: <SwapHorizIcon />,
              path: '/pedidos-trocados',
              badge: trocadosCount,
              roles: ['DESIGNER']
            },
          ]
        },
      ];
    }

    if (user?.role === 'SUPPORT') {
      return [
        {
          title: 'SUPORTE',
          icon: <DashboardIcon />,
          color: '#3b82f6',
          items: [
            {
              text: 'Orçamento Rápido',
              icon: <SpeedIcon />,
              path: '/quick-quote',
              roles: ['SUPPORT']
            },
            {
              text: 'Atendimento Arte',
              icon: <PaletteIcon />,
              path: '/designer-service',
              roles: ['SUPPORT']
            },
            {
              text: 'Desempenho Arte',
              icon: <BarChartIcon />,
              path: '/designer-productivity',
              roles: ['SUPPORT']
            },
          ]
        },
        {
          title: 'OPERAÇÕES',
          icon: <PriorityIcon />,
          color: '#f59e0b',
          items: [
            {
              text: 'Controle Prioridades',
              icon: <PriorityIcon />,
              path: '/priority-control',
              badge: orderCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Pedidos Extraviados',
              icon: <ErrorIcon />,
              path: '/lost-orders',
              badge: lostOrdersCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Pedidos Atrasados',
              icon: <ScheduleIcon />,
              path: '/delayed-orders',
              badge: delayedOrdersCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Erros Internos',
              icon: <CancelIcon />,
              path: '/internal-errors',
              badge: internalErrorsCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Pedidos de Estornos',
              icon: <RefreshIcon />,
              path: '/refund-orders',
              badge: refundCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Verificar Pedido',
              icon: <ManageSearchIcon />,
              path: '/verificar-pedido',
              badge: verificarCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Reposição',
              icon: <Inventory2Icon />,
              path: '/reposicao',
              badge: reposicaoCount,
              roles: ['SUPPORT']
            },
            {
              text: 'Pedidos Trocados',
              icon: <SwapHorizIcon />,
              path: '/pedidos-trocados',
              badge: trocadosCount,
              roles: ['SUPPORT']
            },
          ]
        },
      ];
    }

    if (user?.role === 'MANAGER') {
      return [
        {
          title: 'GERÊNCIA',
          icon: <DashboardIcon />,
          color: '#0d9488',
          items: [
            {
              text: 'Faturamento',
              icon: <ReceiptIcon />,
              path: '/billing',
              roles: ['MANAGER']
            },
            {
              text: 'Orçamento Rápido',
              icon: <SpeedIcon />,
              path: '/quick-quote',
              roles: ['MANAGER']
            },
            {
              text: 'Atendimento Arte',
              icon: <PaletteIcon />,
              path: '/designer-service',
              roles: ['MANAGER']
            },
            {
              text: 'Desempenho Arte',
              icon: <BarChartIcon />,
              path: '/designer-productivity',
              roles: ['MANAGER']
            },
          ]
        },
        {
          title: 'OPERAÇÕES',
          icon: <PriorityIcon />,
          color: '#f59e0b',
          items: [
            {
              text: 'Controle Prioridades',
              icon: <PriorityIcon />,
              path: '/priority-control',
              badge: orderCount,
              roles: ['MANAGER']
            },
            {
              text: 'Pedidos Extraviados',
              icon: <ErrorIcon />,
              path: '/lost-orders',
              badge: lostOrdersCount,
              roles: ['MANAGER']
            },
            {
              text: 'Pedidos Atrasados',
              icon: <ScheduleIcon />,
              path: '/delayed-orders',
              badge: delayedOrdersCount,
              roles: ['MANAGER']
            },
            {
              text: 'Erros Internos',
              icon: <CancelIcon />,
              path: '/internal-errors',
              badge: internalErrorsCount,
              roles: ['MANAGER']
            },
            {
              text: 'Pedidos de Estornos',
              icon: <RefreshIcon />,
              path: '/refund-orders',
              badge: refundCount,
              roles: ['MANAGER']
            },
            {
              text: 'Verificar Pedido',
              icon: <ManageSearchIcon />,
              path: '/verificar-pedido',
              badge: verificarCount,
              roles: ['MANAGER']
            },
            {
              text: 'Reposição',
              icon: <Inventory2Icon />,
              path: '/reposicao',
              badge: reposicaoCount,
              roles: ['MANAGER']
            },
            {
              text: 'Pedidos Trocados',
              icon: <SwapHorizIcon />,
              path: '/pedidos-trocados',
              badge: trocadosCount,
              roles: ['MANAGER']
            },
          ]
        },
      ];
    }

    if (user?.role === 'PRODUCAO') {
      return [
        {
          title: 'PRODUÇÃO',
          icon: <PrecisionManufacturingIcon />,
          color: '#7c3aed',
          items: [
            {
              text: 'Orçamento Rápido',
              icon: <SpeedIcon />,
              path: '/quick-quote',
              roles: ['PRODUCAO']
            },
            {
              text: 'Atendimento Arte',
              icon: <PaletteIcon />,
              path: '/designer-service',
              roles: ['PRODUCAO']
            },
          ]
        },
        {
          title: 'OPERAÇÕES',
          icon: <PriorityIcon />,
          color: '#f59e0b',
          items: [
            {
              text: 'Controle Prioridades',
              icon: <PriorityIcon />,
              path: '/priority-control',
              badge: orderCount,
              roles: ['PRODUCAO']
            },
            {
              text: 'Verificar Pedido',
              icon: <ManageSearchIcon />,
              path: '/verificar-pedido',
              badge: verificarCount,
              roles: ['PRODUCAO']
            },
            {
              text: 'Reposição',
              icon: <Inventory2Icon />,
              path: '/reposicao',
              badge: reposicaoCount,
              roles: ['PRODUCAO']
            },
            {
              text: 'Pedidos Trocados',
              icon: <SwapHorizIcon />,
              path: '/pedidos-trocados',
              badge: trocadosCount,
              roles: ['PRODUCAO']
            },
          ]
        },
      ];
    }

    return [];
  };

  const menuGroups = getMenuGroups();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          gap: 0.5,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          borderBottom: '2px solid rgba(212, 175, 55, 0.3)',
        }}
      >
        <img
          src="/logo.png"
          alt="VettorPrint Adesivos"
          style={{
            maxHeight: 55,
            maxWidth: '100%',
            width: 'auto',
            objectFit: 'contain',
          }}
        />

      </Box>

      <Divider />

      {/* Menu Groups */}
      <List sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {menuGroups.map((group, groupIndex) => (
          <Box key={groupIndex} sx={{ mb: 0.5 }}>
            {/* Group Header */}
            <ListItemButton
              onClick={() => handleGroupClick(group.title.toLowerCase())}
              sx={{
                mx: 1,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(212, 175, 55, 0.1)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: group.color }}>
                {group.icon}
              </ListItemIcon>
              <ListItemText
                primary={group.title}
                primaryTypographyProps={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: 'rgba(212, 175, 55, 0.9)',
                  letterSpacing: '0.5px',
                }}
              />
              {expandedGroups[group.title.toLowerCase()] ? (
                <ExpandLess sx={{ color: 'rgba(212, 175, 55, 0.7)' }} />
              ) : (
                <ExpandMore sx={{ color: 'rgba(212, 175, 55, 0.7)' }} />
              )}
            </ListItemButton>

            {/* Group Items */}
            <Collapse in={expandedGroups[group.title.toLowerCase()]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.items.map((item, itemIndex) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItemButton
                      key={itemIndex}
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        mx: 1,
                        my: 0.25,
                        pl: 4,
                        borderRadius: '8px',
                        backgroundColor: isActive ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
                        borderLeft: isActive ? `3px solid #D4AF37` : '3px solid transparent',
                        '&:hover': {
                          backgroundColor: isActive 
                            ? 'rgba(212, 175, 55, 0.2)' 
                            : 'rgba(212, 175, 55, 0.08)',
                        },
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          minWidth: 36,
                          color: isActive ? '#D4AF37' : 'rgba(255, 255, 255, 0.6)',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '14px',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? '#D4AF37' : 'rgba(255, 255, 255, 0.8)',
                        }}
                      />
                      {item.badge && item.badge > 0 && (
                        <Chip
                          label={item.badge}
                          size="small"
                          sx={{
                            height: '22px',
                            minWidth: '22px',
                            fontSize: '11px',
                            fontWeight: 700,
                            backgroundColor: '#ef4444',
                            color: 'white',
                            borderRadius: '11px',
                            '& .MuiChip-label': {
                              px: 0.75,
                            }
                          }}
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>

      <Divider />

      {/* Settings - Admin e Gerência acessam; demais funções veem "Sem permissão" */}
      <List sx={{ py: 1 }}>
        <ListItemButton
          onClick={() => handleNavigation('/settings')}
          sx={{
            mx: 1,
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: 'rgba(212, 175, 55, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'rgba(255, 255, 255, 0.6)' }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText
            primary="Configurações"
            primaryTypographyProps={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.8)',
            }}
          />
        </ListItemButton>
      </List>

      <Divider sx={{ borderColor: 'rgba(212, 175, 55, 0.1)' }} />

      {/* User Profile Section */}
      <Box sx={{ p: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 1.5,
            borderRadius: '12px',
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
          }}
        >
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40, 
              background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
              color: '#1a1a1a',
              fontWeight: 700,
              boxShadow: '0 2px 4px rgba(212, 175, 55, 0.3)',
            }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600,
                fontSize: '14px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#D4AF37',
              }}
            >
              {user?.name}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '12px',
              }}
            >
              {user?.role === 'ADMIN' ? 'Administrador' : 
               user?.role === 'SELLER' ? 'Vendedor' :
               user?.role === 'DESIGNER' ? 'Designer' :
               user?.role === 'MANAGER' ? 'Gerência' :
               user?.role === 'PRODUCAO' ? 'Produção' : 'Suporte'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {location.pathname !== '/dashboard' && location.pathname !== '/' && (
            <IconButton
              color="inherit"
              onClick={() => navigate(-1)}
              title="Voltar à página anterior"
              sx={{
                mr: 1.5,
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.22)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, color: '#ffffff' }}>
            {menuGroups.flatMap(g => g.items).find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <NotificationBell />
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ 
              ml: 2,
              '&:hover': {
                backgroundColor: 'rgba(212, 175, 55, 0.15)',
              },
            }}
          >
            <Avatar 
              sx={{ 
                width: 36, 
                height: 36, 
                background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)',
                color: '#1a1a1a',
                fontSize: '14px',
                fontWeight: 700,
                boxShadow: '0 2px 4px rgba(212, 175, 55, 0.3)',
              }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => {
              handleProfileMenuClose();
              navigate('/profile');
            }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Perfil
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid rgba(0, 0, 0, 0.08)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* Popup de novas atualizações */}
      <ChangelogModal open={changelogOpen} onClose={handleCloseChangelog} />
    </Box>
  );
};

export default Layout;
